/**
 * EnPrivacyPolicyPage.jsx — Privacy Policy (English)
 * Public route: /en/privacy
 *
 * Required for Azure Marketplace Partner Center submission (Gate 2).
 * EN version of PrivacyPolicyPage.jsx — do NOT edit PT version here.
 *
 * Last updated: May 29, 2026
 */

import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/seo/SEOHead';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD  = '#b48c2c';

const Section = ({ title, children }) => (
  <section className="bg-white rounded-xl border-2 p-6" style={{ borderColor: '#e5e7eb' }}>
    <h2 className="text-2xl font-bold mb-4" style={{ color: BRAND_GREEN }}>{title}</h2>
    {children}
  </section>
);

export default function EnPrivacyPolicyPage() {
  return (
    <>
      <SEOHead
        title="Privacy Policy"
        description="VUKZ AI Privacy Policy — how we collect, use, store and protect your personal data."
        path="/en/privacy"
        lang="en"
        alternatePath="/privacidade"
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/" className="text-sm text-gray-500 hover:underline">vukz.ai</Link>
            <span className="text-gray-300">/</span>
            <span className="text-sm text-gray-700 font-medium">Privacy Policy</span>
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ color: BRAND_GREEN }}>
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500">Last updated: May 29, 2026</p>
          <div className="mt-3 flex gap-3 text-xs">
            <Link to="/privacidade" className="text-gray-400 hover:underline">🇧🇷 Versão em Português</Link>
            <span className="text-gray-300">·</span>
            <Link to="/en/terms" className="hover:underline" style={{ color: BRAND_GREEN }}>Terms of Use</Link>
            <span className="text-gray-300">·</span>
            <Link to="/en/sla" className="hover:underline" style={{ color: BRAND_GREEN }}>SLA</Link>
          </div>
        </div>

        <div className="space-y-6 text-gray-700">

          {/* 1 */}
          <Section title="1. About VUKZ AI">
            <p className="leading-relaxed mb-3">
              We are <strong>VUKZ AI</strong>, legally registered as{' '}
              <strong>Arena Stats Ltda., CNPJ 61.698.906/0001-16 (Brazil)</strong>, located at{' '}
              Av. Presidente Antônio Carlos, 681, Lagoinha, Belo Horizonte / MG, Brazil, ZIP 31210-010.
            </p>
            <p className="leading-relaxed mb-3">
              We develop and operate the <strong>VUKZ platform</strong> — a Sports Intelligence and
              Governance Operating System — offered as a B2B SaaS to clubs, federations, athletes,
              performance staff and sports organizations.
            </p>
            <p className="leading-relaxed">
              For any questions, requests or communications related to this Privacy Policy, please
              contact us at{' '}
              <a href="mailto:privacy@vukz.ai" className="underline" style={{ color: BRAND_GREEN }}>
                privacy@vukz.ai
              </a>{' '}
              or visit <a href="https://www.vukz.ai" className="underline" style={{ color: BRAND_GREEN }}>www.vukz.ai</a>.
            </p>
            <p className="leading-relaxed mt-3 text-sm text-gray-500">
              This Privacy Notice is effective as of May 29, 2026 and applies to data processing
              described on <strong>https://www.vukz.ai</strong> and the platform{' '}
              <strong>https://app.vukz.ai</strong>.
            </p>
          </Section>

          {/* 2 */}
          <Section title="2. What Data We Collect">
            <p className="mb-3">We collect the following categories of personal data:</p>
            <ul className="space-y-2 text-sm">
              {[
                ['Registration data', 'Name, email address, phone number, role (athlete, coach, manager, etc.)'],
                ['Identity data', 'CPF (Brazilian tax ID — encrypted), date of birth, gender'],
                ['Usage data', 'Pages visited, actions performed, session duration, IP address, browser type'],
                ['Sports performance data', 'Times, results, rankings, training sessions, competition data'],
                ['Health & wellness data', 'Wellness questionnaires, training load, recovery indicators (with explicit consent)'],
                ['Payment and subscription data', 'Plan type, subscription status — billing processed by Microsoft Azure Marketplace; we do not store card details'],
                ['Communications', 'Messages sent via support, email or platform chat'],
              ].map(([label, desc]) => (
                <li key={label} className="flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BRAND_GREEN }} />
                  <span><strong>{label}:</strong> {desc}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* 3 */}
          <Section title="3. How We Use Your Data">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Purpose</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Legal basis (LGPD / GDPR)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ['Provide platform services', 'Contract performance'],
                    ['Create and manage user accounts', 'Contract performance'],
                    ['Process Azure Marketplace subscriptions', 'Contract performance / Legal obligation'],
                    ['Deliver performance analytics and AI insights', 'Legitimate interest / Consent'],
                    ['Send operational notifications (incidents, updates)', 'Legitimate interest'],
                    ['Comply with legal obligations (tax, regulatory)', 'Legal obligation'],
                    ['Improve platform features and AI models', 'Legitimate interest (anonymized)'],
                    ['Marketing communications (newsletters)', 'Consent — opt-in only'],
                  ].map(([p, b]) => (
                    <tr key={p}>
                      <td className="px-4 py-2.5 text-gray-700">{p}</td>
                      <td className="px-4 py-2.5 text-gray-500">{b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* 4 */}
          <Section title="4. Data Sharing">
            <p className="mb-3">We do not sell personal data. We may share data with:</p>
            <ul className="space-y-2 text-sm">
              {[
                ['Microsoft Azure', 'Cloud infrastructure (Brazil South region) — hosting, database, monitoring'],
                ['Microsoft Partner Center', 'Subscription management for Azure Marketplace customers'],
                ['Your organization (tenant admin)', 'Access restricted to your institution\'s own data — multi-tenant RLS enforced'],
                ['Legal authorities', 'When required by applicable law or court order'],
              ].map(([who, why]) => (
                <li key={who} className="flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BRAND_GREEN }} />
                  <span><strong>{who}:</strong> {why}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* 5 */}
          <Section title="5. Data Retention">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {[
                { label: 'Active account data', value: 'Retained for the duration of the subscription + 5 years (legal obligation)' },
                { label: 'Sports performance data', value: 'Retained indefinitely (historical record) unless deletion is requested' },
                { label: 'Usage logs', value: '90 days' },
                { label: 'Backup data', value: '30 days rolling' },
                { label: 'Data after account termination', value: 'Anonymized or deleted within 60 days of account closure, except where legally required' },
              ].map(({ label, value }) => (
                <div key={label} className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-semibold text-gray-700 text-xs uppercase tracking-wide">{label}</p>
                  <p className="text-gray-600 mt-1">{value}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* 6 */}
          <Section title="6. Security">
            <p className="leading-relaxed mb-3">
              We implement industry-standard technical and organizational measures to protect your data:
            </p>
            <ul className="space-y-2 text-sm">
              {[
                'Encryption at rest (AES-256) and in transit (TLS 1.2+)',
                'Role-based access control (RBAC) with Row-Level Security (RLS) enforced at the database level',
                'Multi-tenant isolation — each organization sees only its own data',
                'CPF and sensitive identifiers encrypted with ENCRYPTBYKEY()',
                '24/7 monitoring via Microsoft Purview and Azure Monitor',
                'Annual penetration testing by independent third parties',
                'Critical security patches applied within 72 hours',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BRAND_GREEN }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* 7 */}
          <Section title="7. Your Rights">
            <p className="mb-3">
              Under Brazil's LGPD (Lei Geral de Proteção de Dados — Law 13.709/2018) and applicable
              international data protection laws, you have the right to:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {[
                ['Access', 'Request a copy of your personal data'],
                ['Correction', 'Request correction of inaccurate or incomplete data'],
                ['Deletion', 'Request erasure of your data (subject to legal retention obligations)'],
                ['Data portability', 'Receive your data in a structured, machine-readable format'],
                ['Objection', 'Object to processing based on legitimate interest'],
                ['Withdrawal of consent', 'Withdraw consent at any time for consent-based processing'],
                ['Information', 'Know with whom we share your data'],
                ['Complaint', 'Lodge a complaint with Brazil\'s DPA (ANPD) or your local data protection authority'],
              ].map(([right, desc]) => (
                <div key={right} className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-semibold text-gray-800 text-xs uppercase tracking-wide" style={{ color: BRAND_GREEN }}>{right}</p>
                  <p className="text-gray-600 mt-1">{desc}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-gray-600">
              To exercise any of these rights, contact our DPO at{' '}
              <a href="mailto:privacy@vukz.ai" className="underline" style={{ color: BRAND_GREEN }}>privacy@vukz.ai</a>.
              We will respond within 15 business days.
            </p>
          </Section>

          {/* 8 */}
          <Section title="8. Cookies and Tracking">
            <p className="leading-relaxed mb-3">
              VUKZ uses strictly necessary cookies for session management and authentication.
              We do not use third-party advertising cookies. Analytics are processed via
              first-party event tracking.
            </p>
            <p className="leading-relaxed text-sm text-gray-600">
              You may disable cookies in your browser settings, but this may affect platform functionality.
            </p>
          </Section>

          {/* 9 */}
          <Section title="9. International Data Transfers">
            <p className="leading-relaxed">
              Your data is primarily stored in <strong>Microsoft Azure — Brazil South region</strong>.
              For disaster recovery purposes, encrypted backups are replicated to Azure US East region.
              All transfers comply with LGPD requirements for international data transfer (Article 33).
              Microsoft Azure is certified under ISO 27001, SOC 2, and EU Standard Contractual Clauses.
            </p>
          </Section>

          {/* 10 */}
          <Section title="10. Minors">
            <p className="leading-relaxed">
              The VUKZ platform may process data of athletes under 18 years of age for
              sports performance tracking purposes. This requires <strong>explicit consent</strong>{' '}
              from a parent or legal guardian, confirmed at registration. Data of minors
              is subject to additional protections under LGPD Article 14.
            </p>
          </Section>

          {/* 11 */}
          <Section title="11. Changes to This Policy">
            <p className="leading-relaxed">
              We may update this Privacy Policy periodically. Material changes will be communicated
              via email to registered users and/or via a prominent notice on the platform at least{' '}
              <strong>15 days before</strong> the changes take effect.
              Continued use of the platform after the effective date constitutes acceptance of the
              updated policy.
            </p>
          </Section>

          {/* 12 — Contact */}
          <Section title="12. Contact & DPO">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="font-bold text-gray-800 mb-2">Data Protection Officer (DPO)</p>
                <p className="text-gray-600">Paulo Brandt</p>
                <a href="mailto:privacy@vukz.ai" className="underline mt-1 block" style={{ color: BRAND_GREEN }}>
                  privacy@vukz.ai
                </a>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="font-bold text-gray-800 mb-2">Legal Entity</p>
                <p className="text-gray-600">Arena Stats Ltda.</p>
                <p className="text-gray-600">CNPJ 61.698.906/0001-16</p>
                <p className="text-gray-600 text-xs mt-1">
                  Av. Presidente Antônio Carlos, 681<br />
                  Lagoinha, Belo Horizonte / MG, Brazil<br />
                  ZIP 31210-010
                </p>
              </div>
            </div>
          </Section>

          {/* Footer */}
          <div className="pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
            <p>VUKZ AI · Arena Stats Ltda. · CNPJ 61.698.906/0001-16 · Belo Horizonte, Brazil</p>
            <p className="mt-2">
              <Link to="/en/terms" className="hover:underline mx-2" style={{ color: BRAND_GREEN }}>Terms of Use</Link>
              ·
              <span className="mx-2 font-semibold" style={{ color: BRAND_GREEN }}>Privacy Policy (this document)</span>
              ·
              <Link to="/en/sla" className="hover:underline mx-2" style={{ color: BRAND_GREEN }}>SLA</Link>
            </p>
            <p className="mt-2 text-xs text-gray-400">Version 1.0 — May 29, 2026</p>
          </div>

        </div>
      </div>
    </>
  );
}
